/**
 * ConversationIntelligencePanel — the always-on right column of the reading zone.
 *
 * Turns the reading pane from an email viewer into a Legal Communication Workspace:
 * it surfaces the intelligence already derived by the engines (legal signals,
 * awaiting-reply, suggested link, deadline) and anchors the linked matter.
 *
 * Everything here is LEGAL, PRACTICAL, EXPLAINABLE, AUDITABLE — derived from real
 * thread data. No analytics, no AI summaries, no sentiment, no "magic insights".
 * Signals show only when true (no decorative placeholders).
 */

import { Scales, Gavel, CalendarX, CurrencyDollar, ClockCountdown, ShieldWarning } from '@phosphor-icons/react';
import { WorkspaceMatterPanel } from './WorkspaceMatterPanel';
import type { ConversationSummary } from '@/lib/inboxApi';

const font = "'IBM Plex Sans', sans-serif";

interface MatterRef { id: string; caseNumber: string; caseName: string }

interface Props {
  conversation: ConversationSummary | null;
  /** Matter context when an email (not a conversation) is selected — fallback anchor. */
  fallbackMatter: MatterRef | null;
  onAccept: (matterId: string) => void;
  onDismiss: () => void;
  onChange: () => void;
  onCreateTask: (matterId: string) => void;
  onCreateHearingNote: (matterId: string) => void;
  onLogTime: (matterId: string) => void;
  onSaveToDocuments: (matterId: string) => void;
  onGenerateFeeEntry: (matterId: string) => void;
}

// ─── helpers ───────────────────────────────────────────────────

function relativeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return days === 1 ? '1 day ago' : `${days} days ago`;
  const hrs = Math.floor(ms / 3_600_000);
  if (hrs >= 1) return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return mins === 1 ? '1 minute ago' : `${mins} minutes ago`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function deadlineCountdown(iso: string): { label: string; urgent: boolean } {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { label: 'Overdue', urgent: true };
  if (days === 0) return { label: 'Due today', urgent: true };
  if (days === 1) return { label: '1 day remaining', urgent: true };
  return { label: `${days} days remaining`, urgent: days <= 5 };
}

// Legal signal chip definitions — only rendered when the signal is true.
const SIGNAL_STYLE = {
  wp:       { label: 'WP',             Icon: ShieldWarning, fg: '#b45309', bg: 'rgba(245,158,11,0.15)', bd: 'rgba(245,158,11,0.35)' },
  hearing:  { label: 'HEARING',        Icon: Gavel,         fg: '#6d28d9', bg: '#f5f3ff', bd: '#ddd6fe' },
  deadline: { label: 'DEADLINE',       Icon: CalendarX,     fg: '#dc2626', bg: '#fef2f2', bd: '#fecaca' },
  billing:  { label: 'BILLING',        Icon: CurrencyDollar,fg: '#047857', bg: '#ecfdf5', bd: '#a7f3d0' },
  awaiting: { label: 'AWAITING REPLY', Icon: ClockCountdown,fg: '#1d4ed8', bg: '#eff6ff', bd: '#bfdbfe' },
} as const;

const sectionLabel: React.CSSProperties = {
  fontFamily: font, fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)',
  textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 7px',
};

function Chip({ kind }: { kind: keyof typeof SIGNAL_STYLE }) {
  const s = SIGNAL_STYLE[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 4, background: s.bg, border: `1px solid ${s.bd}`,
      fontFamily: font, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: s.fg,
    }}>
      <s.Icon size={11} weight="fill" /> {s.label}
    </span>
  );
}

const block: React.CSSProperties = {
  border: '1px solid var(--border-subtle)', borderRadius: 7, padding: '10px 12px', marginBottom: 10,
  background: 'var(--surface-subtle)',
};

// ─── panel ─────────────────────────────────────────────────────

export function ConversationIntelligencePanel({
  conversation, fallbackMatter, onAccept, onDismiss, onChange,
  onCreateTask, onCreateHearingNote, onLogTime, onSaveToDocuments, onGenerateFeeEntry,
}: Props) {
  const c = conversation;
  const matter = c?.matter ?? fallbackMatter;
  const isSuggested = c?.linkStatus === 'suggested' && !!c?.matter;

  const signals: (keyof typeof SIGNAL_STYLE)[] = [];
  if (c?.isWithoutPrej) signals.push('wp');
  if (c?.hasHearingRef) signals.push('hearing');
  if (c?.hasDeadline) signals.push('deadline');
  if (c?.isBillingRelevant) signals.push('billing');
  if (c?.awaitingReply) signals.push('awaiting');

  const hasIntel = signals.length > 0 || isSuggested || !!c?.awaitingReply || (c?.hasDeadline ?? false);
  const showEmpty = !matter && !hasIntel;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-card)', overflow: 'hidden' }}>
      {/* Intelligence section (signals + blocks) — flex-shrink so the matter card fills below */}
      {(hasIntel || (!matter && !!c)) && (
        <div style={{ flexShrink: 0, padding: '14px 14px 4px', borderBottom: matter ? '1px solid var(--border-default)' : 'none', maxHeight: '55%', overflowY: 'auto' }}>
          {signals.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p style={sectionLabel}>Signals</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {signals.map((k) => <Chip key={k} kind={k} />)}
              </div>
            </div>
          )}

          {/* Suggested matter link — Accept / Dismiss */}
          {isSuggested && c?.matter && (
            <div style={{ ...block, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p style={{ ...sectionLabel, color: '#15803d' }}>Possible matter match</p>
              <div style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: '#166534', lineHeight: 1.35 }}>
                {c.matter.caseNumber} — {c.matter.caseName}
              </div>
              {typeof c.linkConfidence === 'number' && (
                <div style={{ fontFamily: font, fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                  {Math.round(c.linkConfidence * 100)}% confidence
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                <button
                  onClick={() => onAccept(c.matter!.id)}
                  style={{ flex: 1, padding: '6px 0', borderRadius: 5, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontFamily: font, fontSize: 12, fontWeight: 600 }}
                >
                  Accept
                </button>
                <button
                  onClick={onDismiss}
                  style={{ flex: 1, padding: '6px 0', borderRadius: 5, border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: font, fontSize: 12 }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Deadline block — real extracted date + countdown */}
          {c?.hasDeadline && (
            <div style={{ ...block, background: '#fef2f2', border: '1px solid #fecaca' }}>
              <p style={{ ...sectionLabel, color: '#b91c1c' }}>Deadline</p>
              {c.nextDeadlineAt ? (
                <>
                  <div style={{ fontFamily: font, fontSize: 14, fontWeight: 600, color: '#991b1b' }}>
                    {fmtDate(c.nextDeadlineAt)}
                  </div>
                  <div style={{ fontFamily: font, fontSize: 11, fontWeight: 600, color: deadlineCountdown(c.nextDeadlineAt).urgent ? '#dc2626' : '#9a3412', marginTop: 2 }}>
                    {deadlineCountdown(c.nextDeadlineAt).label}
                  </div>
                </>
              ) : (
                <div style={{ fontFamily: font, fontSize: 12, color: '#9a3412' }}>
                  Mentioned in this thread
                </div>
              )}
            </div>
          )}

          {/* Awaiting reply block — since / last outbound */}
          {c?.awaitingReply && c.awaitingReplySince && (
            <div style={{ ...block, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <p style={{ ...sectionLabel, color: '#1d4ed8' }}>Awaiting reply</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: font, fontSize: 12, color: 'var(--text-primary)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Since</span>
                <span style={{ fontWeight: 600 }}>{relativeAgo(c.awaitingReplySince)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: font, fontSize: 12, color: 'var(--text-primary)', marginTop: 3 }}>
                <span style={{ color: 'var(--text-muted)' }}>Last outbound</span>
                <span style={{ fontWeight: 600 }}>{fmtDate(c.awaitingReplySince)}</span>
              </div>
            </div>
          )}

          {/* No matter yet, but we have a conversation — invite linking */}
          {!matter && c && (
            <button
              onClick={onChange}
              style={{ width: '100%', padding: '8px 0', marginTop: 2, marginBottom: 8, borderRadius: 5, border: '1px dashed var(--border-strong)', background: 'var(--surface-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: font, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Scales size={13} /> Link to a matter
            </button>
          )}
        </div>
      )}

      {/* Matter card (anchor) — reuse the existing, tested panel */}
      {matter ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <WorkspaceMatterPanel
            inline
            isOpen
            matterId={matter.id}
            matterName={matter.caseName}
            matterNumber={matter.caseNumber}
            onClose={() => {}}
            onCreateTask={() => onCreateTask(matter.id)}
            onCreateHearingNote={() => onCreateHearingNote(matter.id)}
            onLogTime={() => onLogTime(matter.id)}
            onSaveToDocuments={() => onSaveToDocuments(matter.id)}
            onGenerateFeeEntry={() => onGenerateFeeEntry(matter.id)}
          />
        </div>
      ) : showEmpty ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', gap: 10 }}>
          <Scales size={26} color="#cbd5e1" />
          <div style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: 'var(--text-subtle)' }}>
            Communication intelligence
          </div>
          <div style={{ fontFamily: font, fontSize: 12, color: '#9ca3af', lineHeight: 1.5, maxWidth: 220 }}>
            Open a conversation to see its matter, legal signals, deadlines and reply status.
          </div>
        </div>
      ) : null}
    </div>
  );
}
